import * as React from 'react';

import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextInput,
  Typography,
} from '@botco/library';
import { PublishIcon } from '@botco/library/icons';
import { UploadImageModal } from '~/components/UploadImageModal';

import { useCreateAgentModal } from './useCreateAgentModal';

type Props = {
  modalOpen: boolean;
  closed: () => void;
  fullScreen?: boolean;
};

const AVATAR_CONFIG = {
  maxWidth: 200,
  maxHeight: 200,
};

export const CreateAgentModal: React.FC<Props> = ({
  modalOpen,
  closed,
  fullScreen = false,
}) => {
  const {
    agentName,
    avatarUrl,
    showImageDialog,
    nameError,
    isLoading,
    isFormValid,
    handleNameChange,
    handleSubmit,
    handleOpenImageDialog,
    handleCloseImageDialog,
    handleAvatarChange,
    handleClose,
    validateName,
  } = useCreateAgentModal({ onClose: closed });

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        open={modalOpen}
        onClose={handleClose}
      >
        <DialogContent>
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <Typography
              align="center"
              variant="h6"
              fontWeight={600}
              color="primary"
            >
              Create Chatbot!
            </Typography>

            <Box mb={2}>
              <Typography fontWeight={600} variant="body2">
                Chatbot name
              </Typography>
              <TextInput
                fullWidth
                placeholder="Agent name"
                onBlur={() => validateName(agentName)}
                value={agentName}
                onChange={handleNameChange}
                helperText="e.g. NIKE"
                error={nameError}
                inputProps={{ autoFocus: true }}
              />
            </Box>

            <Box mt={2}>
              <Typography fontWeight={600} variant="body2">
                Upload chatbot avatar
              </Typography>
              <Stack direction="row" alignItems="center" gap={2} my={2}>
                {avatarUrl && (
                  <Avatar
                    sx={{ border: '1px solid', borderColor: 'primary.main' }}
                    src={avatarUrl}
                  />
                )}
                <IconButton size="small" onClick={handleOpenImageDialog}>
                  <PublishIcon fontSize="large" color="primary" />
                </IconButton>
                {!avatarUrl && (
                  <Typography variant="caption" color="textSecondary">
                    (Images should be {AVATAR_CONFIG.maxWidth}px x{' '}
                    {AVATAR_CONFIG.maxHeight}px, ideally uploaded as a square.)
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box display="flex" justifyContent="center" mt={2} gap={2}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isFormValid || isLoading}>
                {isLoading ? <CircularProgress size={16} /> : 'Save'}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>

      <UploadImageModal
        open={showImageDialog}
        onClose={handleCloseImageDialog}
        src={avatarUrl}
        onChange={handleAvatarChange}
        config={AVATAR_CONFIG}
        title="Upload Chatbot Avatar"
      />
    </>
  );
};
